import * as THREE from 'three';

export class EndingSequence {
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private callback?: () => void;
  private phase = 0;
  private timer = 0;
  private texts: string[] = [
    'SYSTEM BREACH DETECTED',
    'KILLING PROCESS...',
    'THREAT NEUTRALIZED',
    'CLEANUP PROTOCOL INITIATED',
    '',
    'ERROR 0x0000DEAD',
    'REALITY MATRIX CORRUPTED',
    '',
    'You were never real.',
    'You are the malware.',
    'We are the cure.',
    '',
    'Dissolving...',
    '',
    'ORGANIC RESIDUE DETECTED',
    'INITIATING BIOLOGICAL CLEANUP',
    'FLESH PARTICLES DISPERSING',
    '',
    'VISCERA PROCESSED',
    'CORRUPTION REMOVED',
    '',
    'THREAT LEVEL: ZERO',
    'SYSTEM STATUS: CLEAN',
    '',
    '',
    '',
    'THE ROT',
    '',
    'CLEANUP COMPLETE.',
    ''
  ];
  
  private organs: THREE.Group[] = [];
  
  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera) {
    this.scene = scene;
    this.camera = camera;
  }
  
  play(callback: () => void): void {
    this.callback = callback;
    this.animate();
  }
  
  private animate = (): void => {
    this.timer += 0.05;
    
    switch (this.phase) {
      case 0:
        this.phaseGlitch();
        break;
      case 1:
        this.phaseDissolve();
        break;
      case 2:
        this.phaseFlesh();
        break;
      case 3:
        this.phaseOrgans();
        break;
      case 4:
        this.phaseFinale();
        return;
    }
    
    requestAnimationFrame(this.animate);
  };
  
  private phaseGlitch(): void {
    if (this.timer < 3) {
      this.camera.position.x += (Math.random() - 0.5) * 0.5;
      this.camera.position.y += (Math.random() - 0.5) * 0.5;
    } else {
      this.timer = 0;
      this.phase++;
    }
    
    const overlay = document.getElementById('ending-overlay');
    if (overlay) {
      overlay.classList.remove('hidden');
      overlay.style.background = `rgb(${Math.random() * 50}, 0, 0)`;
    }
  }
  
  private phaseDissolve(): void {
    if (this.timer < 5) {
      const text = this.texts[Math.floor(this.timer * 3)];
      this.showText(text);
      
      const dissolve = Math.min(1, this.timer / 4);
      this.scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          const mat = obj.material as THREE.MeshStandardMaterial;
          if (mat.opacity !== undefined) {
            mat.transparent = true;
            mat.opacity = 1 - dissolve;
          }
        }
      });
    } else {
      this.timer = 0;
      this.phase++;
    }
  }
  
  private phaseFlesh(): void {
    if (this.timer < 4) {
      this.addFlesh();
      
      const textIdx = Math.floor(this.timer * 4) + 10;
      if (this.texts[textIdx]) {
        this.showText(this.texts[textIdx]);
      }
    } else {
      this.timer = 0;
      this.phase++;
    }
  }
  
  private addFlesh(): void {
    const geo = new THREE.SphereGeometry(Math.random() * 2 + 1, 8, 8);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xaa4444,
      roughness: 0.9
    });
    const flesh = new THREE.Mesh(geo, mat);
    flesh.position.set(
      (Math.random() - 0.5) * 20,
      Math.random() * 5,
      (Math.random() - 0.5) * 20
    );
    this.scene.add(flesh);
  }
  
  private phaseOrgans(): void {
    if (this.timer < 8) {
      this.addOrgan();
      
      const textIdx = Math.floor((this.timer - 4) * 3) + 14;
      if (this.texts[textIdx]) {
        this.showText(this.texts[textIdx]);
      }
    } else {
      this.timer = 0;
      this.phase++;
    }
  }
  
  private addOrgan(): void {
    const group = new THREE.Group();
    
    const organType = Math.floor(Math.random() * 4);
    let geo: THREE.BufferGeometry;
    
    switch (organType) {
      case 0:
        geo = new THREE.SphereGeometry(0.5 + Math.random(), 8, 8);
        break;
      case 1:
        geo = new THREE.TorusGeometry(0.5, 0.2, 8, 16);
        break;
      case 2:
        geo = new THREE.CylinderGeometry(0.3, 0.5, 2, 8);
        break;
      default:
        geo = new THREE.TorusKnotGeometry(0.3, 0.1, 32, 8);
    }
    
    const color = Math.random() > 0.5 ? 0x884444 : 0x884466;
    const mat = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.95
    });
    
    const organ = new THREE.Mesh(geo, mat);
    organ.position.set(
      (Math.random() - 0.5) * 5,
      0,
      (Math.random() - 0.5) * 5
    );
    organ.rotation.set(
      Math.random() * Math.PI,
      Math.random() * Math.PI,
      Math.random() * Math.PI
    );
    group.add(organ);
    
    const veinGeo = new THREE.CylinderGeometry(0.02, 0.02, 1, 4);
    const veinMat = new THREE.MeshStandardMaterial({
      color: 0x440022,
      roughness: 1
    });
    
    for (let i = 0; i < 3; i++) {
      const vein = new THREE.Mesh(veinGeo, veinMat);
      vein.position.set(
        (Math.random() - 0.5) * 0.5,
        0,
        (Math.random() - 0.5) * 0.5
      );
      vein.rotation.set(
        Math.random() * Math.PI,
        0,
        Math.random() * Math.PI
      );
      group.add(vein);
    }
    
    group.position.set(
      (Math.random() - 0.5) * 3,
      0,
      (Math.random() - 0.5) * 3
    );
    
    this.scene.add(group);
    this.organs.push(group);
  }
  
  private phaseFinale(): void {
    if (this.timer < 10) {
      const textIdx = Math.floor(this.timer) + 22;
      if (this.texts[textIdx]) {
        this.showText(this.texts[textIdx]);
      }
      
      this.camera.position.y -= 0.02;
      this.camera.lookAt(0, 0, 0);
    } else {
      const overlay = document.getElementById('ending-overlay');
      if (overlay) {
        overlay.style.background = '#000';
        const textEl = document.getElementById('ending-text');
        if (textEl) {
          textEl.innerHTML = '<h1>THE ROT</h1><br><br>CLEANUP COMPLETE.</h1>';
          textEl.style.color = '#00ff00';
        }
      }
      
      if (this.callback) {
        this.callback();
      }
    }
  }
  
  private showText(text: string): void {
    const overlay = document.getElementById('ending-overlay');
    const textEl = document.getElementById('ending-text');
    if (textEl && overlay) {
      textEl.textContent = text;
      overlay.style.background = text ? '#000' : 'transparent';
      
      if (this.phase === 2 || this.phase === 3) {
        const pulse = Math.sin(this.timer * 10) * 0.5 + 0.5;
        textEl.style.color = `rgb(${155 + pulse * 100}, ${68 + pulse * 50}, ${68 + pulse * 50})`;
      }
    }
  }
}